"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { badgeColors } from "@/lib";
import { cn } from "@/lib/utils";
import { FaChevronDown, FaChevronUp, FaPen, FaXmark } from "react-icons/fa6";

export type BadgeItem = {
  id: string;
  name: string;
  secondValue?: string;
};

export type MobileCollapsibleBadgesProps = {
  items: BadgeItem[];
  sectionTitle: string;
  onItemClick?: (itemId: string) => void;
  onItemRemove?: (itemId: string) => void;
  maxPreviewCount?: number;
  disabled?: boolean;
  className?: string;
};

export function MobileCollapsibleBadges({
  items,
  sectionTitle,
  onItemClick,
  onItemRemove,
  maxPreviewCount = 2,
  disabled = false,
  className = "",
}: MobileCollapsibleBadgesProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) {
    return null;
  }

  const hasMore = items.length > maxPreviewCount;
  const displayItems = isExpanded ? items : items.slice(0, maxPreviewCount);
  const remainingCount = items.length - maxPreviewCount;

  return (
    <div className={cn("md:hidden", className)}>
      {/* Mobile Summary/Header */}
      <div
        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <FaPen className="text-gray-500 w-4 h-4" />
          <span className="font-medium text-sm text-gray-700">
            {sectionTitle} ({items.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && hasMore && (
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
              +{remainingCount} more
            </span>
          )}
          {isExpanded ? (
            <FaChevronUp className="text-gray-500 w-4 h-4" />
          ) : (
            <FaChevronDown className="text-gray-500 w-4 h-4" />
          )}
        </div>
      </div>

      {/* Preview badges (collapsed state) */}
      {!isExpanded && items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {displayItems.map((item, idx) => (
            <Badge
              key={`preview-${idx}-${item.id || ''}`}
              className={cn(
                "text-xs px-2 py-1",
                badgeColors[idx % badgeColors.length],
                "cursor-default"
              )}
            >
              {item.name}
              {item.secondValue && (
                <span className="ml-1 opacity-75">({item.secondValue})</span>
              )}
            </Badge>
          ))}
          {hasMore && (
            <div key="more-indicator" className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded-full">
              ...
            </div>
          )}
        </div>
      )}

      {/* Full badges list (expanded state) */}
      {isExpanded && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-gray-500 mb-2">
            Tap badges to edit • Tap X to remove
          </div>
          <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
            {items.map((item, idx) => (
              <Badge
                key={`expanded-${idx}-${item.id || ''}`}
                className={cn(
                  "flex items-center gap-1 pr-1 px-3 py-2 text-sm",
                  badgeColors[idx % badgeColors.length]
                )}
              >
                <span
                  className={cn(
                    "flex-1 select-none",
                    onItemClick && !disabled ? "cursor-pointer" : "cursor-default"
                  )}
                  onClick={disabled ? undefined : () => onItemClick?.(item.id)}
                >
                  {item.name}
                  {item.secondValue && (
                    <span className="ml-1 opacity-75">({item.secondValue})</span>
                  )}
                </span>
                {!disabled && onItemRemove && (
                  <button
                    type="button"
                    className="h-4 w-4 ml-1 flex items-center justify-center hover:opacity-80"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onItemRemove(item.id);
                    }}
                  >
                    <FaXmark className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>

          {/* Collapse button */}
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="w-full py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Collapse
          </button>
        </div>
      )}
    </div>
  );
}
